import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  STORAGE_KEY,
  isConsentRequiredRegion,
  loadGoogleAds,
  updateConsent,
} from "@/lib/consent";
import { trackInternalEvent } from "@/lib/internal-analytics";

export const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    void loadGoogleAds();

    if (localStorage.getItem(STORAGE_KEY) !== null) return;
    let active = true;
    void isConsentRequiredRegion().then((required) => {
      if (active && required && localStorage.getItem(STORAGE_KEY) === null) {
        setVisible(true);
        void trackInternalEvent("consent_banner_view");
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY || event.newValue === null) return;
      updateConsent(event.newValue === "granted" ? "granted" : "denied");
      setVisible(false);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const decide = (decision: "granted" | "denied") => {
    localStorage.setItem(STORAGE_KEY, decision);
    updateConsent(decision);
    void trackInternalEvent(decision === "granted" ? "consent_granted" : "consent_denied");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-description"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background p-4 shadow-lg md:p-6"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-1">
          <h2 id="cookie-consent-title" className="text-base font-semibold text-foreground">
            We use cookies for advertising measurement
          </h2>
          <p id="cookie-consent-description" className="text-sm text-muted-foreground">
            We measure how visitors reach this site so our ads stay relevant. You can
            decline and still use every page. Read our{" "}
            <a href="/privacy" className="underline underline-offset-2 hover:text-foreground">
              privacy policy
            </a>{" "}
            and{" "}
            <a
              href="https://business.safety.google/privacy/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-foreground"
            >
              how Google uses data
            </a>
            .
          </p>
        </div>
        <div className="flex flex-shrink-0 gap-3">
          <Button
            variant="outline"
            className="min-h-[44px] flex-1 md:flex-none"
            onClick={() => decide("denied")}
          >
            Decline
          </Button>
          <Button
            className="min-h-[44px] flex-1 md:flex-none"
            onClick={() => decide("granted")}
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
};
